import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { PaymentMethod } from '@modals/Billing/TopUpCreditBalance/components/PaymentMethod';
import { Plans } from '@modals/Billing/TopUpCreditBalance/components/Plans';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { formatPrice } from '@helpers/formatPrice';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { BillingAccountService, GetBillingAccountResponseDto, PaymentService } from 'app/API';
import { TPaymentMethod } from 'app/types/entities/TPaymentMethod';
import { ApiCallError } from 'app/http/ApiCallError';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { Content } from './styles/Content';
import { TProps } from './types/TProps';

export const TopUpCreditBalanceModal = ({ billingAccountId, onCancel, onSuccess, initialAmount = 10 }: TProps) => {
  const { closeModal, openModal } = useModalContext();
  const { call } = useAPI();
  const { organization } = useAuthContext();
  const baId = billingAccountId || organization?.billingAccount.id || '';
  let initAmount = initialAmount;
  if (initAmount < 10) {
    initAmount = 10;
  }
  const [amount, setAmount] = useState(initAmount);
  const [billingAccount, setBillingAccount] = useState<GetBillingAccountResponseDto>();
  const [rate, setRate] = useState(parseFloat((billingAccount?.tierRate ?? 0).toString()));
  const [promoRate, setPromoRate] = useState(0);
  const [resultAmount, setResultAmount] = useState((billingAccount?.currentBalance ?? 0) / 100 + 10);
  const [paymentMethods, setPaymentMethods] = useState<{
    default: TPaymentMethod;
    all: TPaymentMethod[];
  }>();
  const [isLoading, setIsLoading] = useState(false);

  const onlyXLeftPosition = ((amount - 10) / 2570) * 100;
  function calculateNextTier() {
    if (rate < 11) {
      return { nextTierLeft: 0, nextTierName: '', showOnlyXLeft: false };
    }
    if (rate > 10 && resultAmount > 1750) {
      return { nextTierLeft: Math.ceil(2000 - resultAmount), nextTierName: 'Business', showOnlyXLeft: true };
    }
    if (rate > 11 && resultAmount > 750) {
      return { nextTierLeft: Math.ceil(1000 - resultAmount), nextTierName: 'Agency', showOnlyXLeft: true };
    }
    if (rate > 12 && resultAmount > 400) {
      return { nextTierLeft: Math.ceil(500 - resultAmount), nextTierName: 'Pro', showOnlyXLeft: true };
    }
    if (rate > 13.5 && resultAmount > 50) {
      return { nextTierLeft: Math.ceil(100 - resultAmount), nextTierName: 'Freelancer', showOnlyXLeft: true };
    }
    return { nextTierLeft: 0, nextTierName: '', showOnlyXLeft: false };
  }
  const { nextTierLeft, nextTierName, showOnlyXLeft } = calculateNextTier();

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const nextBillingAccount = await call(() => BillingAccountService.findOne({ baId }));
        setBillingAccount(nextBillingAccount);
        const nextPaymentMethods = await call(() => PaymentService.billingAccountPaymentMethodList({ baId }));

        setPaymentMethods({
          default: nextPaymentMethods.find((item) => item.default) || nextPaymentMethods[0],
          all: nextPaymentMethods,
        });
      } catch (error: unknown) {
        if (error instanceof ApiCallError && error.status === 401) {
          return;
        }
        if (error instanceof ApiCallError && error.status === 403) {
          toast.error('You do not have access to this billing account');
          return;
        }
        toast.error('Unable to load payment methods. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, []);

  useEffect(() => {
    if (!billingAccount) {
      return;
    }
    const defaultRate = parseFloat((billingAccount?.tierRate ?? 0).toString());
    const currentBalance = (billingAccount.currentBalance ?? 0) / 100;
    const newAmount = amount + currentBalance;

    // If fixed rate is set
    if (billingAccount?.fixedRate) {
      setRate(defaultRate);
      setPromoRate(0);
      setResultAmount(newAmount);
      return;
    }

    // Otherwise, calculate the rate based on the amount
    let newRate = defaultRate;
    if (newAmount >= 2000) {
      newRate = 10;
    } else if (newAmount >= 1000) {
      newRate = 11;
    } else if (newAmount >= 500) {
      newRate = 12;
    } else if (newAmount >= 100) {
      newRate = 13.5;
    }
    setResultAmount(newAmount);
    setRate(newRate);
  }, [amount, billingAccount]);

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      await call(
        () =>
          PaymentService.createPaymentIntent({
            baId,
            requestBody: {
              amount: amount * 100,
              balanceTopup: true,
            },
          }),
        {
          onSuccess: (result) => {
            if (['succeeded', 'processing'].includes(result.status)) {
              const latestCharge: unknown = result.latest_charge;
              openModal(ModalNamesEnum.TransactionStatus, {
                billingAccountId: baId,
                chargeId: typeof latestCharge === 'string' && latestCharge.length > 0 ? latestCharge : undefined,
                paymentIntentId: result.id,
                pics: result.client_secret,
                onSuccess,
              });
              setIsLoading(false);
              return;
            }
            // 3D Secure
            // todo: maybe make it open in a modal with an iframe???
            if (result.status === 'requires_action' && result.next_action?.type === 'redirect_to_url') {
              setIsLoading(false);
              window.location.href = (result.next_action?.redirect_to_url as { url: string }).url;
              return;
            }
            if ((result.status ?? '').includes('requires_')) {
              // requires_
              openModal(ModalNamesEnum.Status, {
                title: 'Payment Status',
                message: (
                  <>
                    Something went wrong. Please try again later <br /> (Status code: {result.status})
                  </>
                ),
                status: 'error',
                buttonMessage: 'Ok',
                onSuccess,
              });
              setIsLoading(false);
              return;
            }
            // canceled?
            setIsLoading(false);
            closeModal();
            onSuccess();
          },
          onError: (error) => {
            openModal(ModalNamesEnum.Status, {
              title: 'Payment Status',
              message: (
                <>
                  Something went wrong. Please try again later <br /> Error: {error}
                </>
              ),
              status: 'error',
              buttonMessage: 'Ok',
              onSuccess,
            });
            setIsLoading(false);
          },
        },
      );
    } catch (error: unknown) {
      if (!(error instanceof ApiCallError)) {
        toast.error('Unable to complete the top-up. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitDisabled = isLoading || !paymentMethods?.default || !Number.isFinite(amount) || amount < 10;

  const renderAmountLine = () => {
    return (
      <div className="amount-line">
        <div className="input-wrapper">
          <div className="label">Amount (USD) *</div>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (!e.target.value.length) {
                setAmount(0);
                return;
              }
              const nextAmount = parseInt(e.target.value, 10);
              if (!Number.isFinite(nextAmount)) {
                return;
              }
              setAmount(nextAmount);
            }}
            label="Amount"
            value={amount}
          />
        </div>
        <div className="input-wrapper">
          <div className="label">Balance after top-up</div>
          <div className="balance">{formatPrice(resultAmount)}</div>
        </div>
        <div className="input-wrapper">
          <div className="label">Estimated audio hours</div>
          <div className="balance">
            {promoRate && rate ? (
              <span>{Math.floor(resultAmount / rate)}</span>
            ) : rate ? (
              Math.floor(resultAmount / rate)
            ) : (
              'Loading...'
            )}
          </div>
        </div>
        <div className="input-wrapper new-rate">
          <div className="label">Your new rate</div>
          <div className="balance">
            {promoRate && rate ? (
              <span>{`$${rate.toFixed(2).toString()}`}</span>
            ) : rate ? (
              `$${rate.toFixed(2).toString()}`
            ) : (
              'Loading...'
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <ModalWrapper width="780px" overflow="visible">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Prepaid Credit Balance</ModalTitle>

      <Content>
        <div className="input-wrapper">
          <div className="label">Payment method</div>
          <PaymentMethod defaultPaymentMethod={paymentMethods?.default} isLoading={isLoading} />
        </div>
        <div className="input-wrapper">
          <div className="label">Rate</div>
          <Plans
            amount={amount}
            billingAccount={billingAccount}
            nextTierLeft={nextTierLeft}
            nextTierName={nextTierName}
            onlyXLeftPosition={onlyXLeftPosition}
            rate={rate}
            setAmount={setAmount}
            showOnlyXLeft={showOnlyXLeft}
          />
        </div>

        {renderAmountLine()}

        <div className="description">* Note: minimum top up amount is $10</div>
        <div className="description">** Balance updates automatically shortly after the payment completes</div>

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={isSubmitDisabled} onClick={onSubmit}>
            Pay now
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
